/**
 * Admin — page « Utilisateurs » (migration 0306) : liste, détail des accès,
 * offrir / retirer un pass gratuit (edge fn `admin-user-access`).
 * RPC postérieures aux types générés → client générique.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { AdminUserRow, AdminUserSort, GiftAccessType } from './user-gifts';

const db = supabase as unknown as SupabaseClient;

export function useAdminUsers(params: { query: string; role: string | null; sort: AdminUserSort }) {
  return useQuery({
    queryKey: ['admin-users', params.query, params.role, params.sort],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<AdminUserRow[]> => {
      // v2 (migration 0307) : + dernière activité, tri fait en base.
      const { data, error } = await db.rpc('admin_list_users_v2', {
        p_query: params.query.trim() || null,
        p_role: params.role,
        p_limit: 1000,
        p_sort: params.sort,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as AdminUserRow[];
    },
  });
}

export interface AdminUserPass {
  id: string;
  type: GiftAccessType;
  source: string;
  expires_at: string;
  created_at: string;
}

export interface AdminUserSubscription {
  id: string;
  plan: string;
  status: string;
  provider: string | null;
  current_period_end: string | null;
  is_owner: boolean;
}

export interface AdminUserGrant {
  id: string;
  access_right_id: string | null;
  access_type: GiftAccessType;
  duration_days: number;
  expires_at: string;
  message_title: string | null;
  message_body: string | null;
  push_sent: boolean;
  email_sent: boolean;
  revoked_at: string | null;
  created_at: string;
  granted_by_name: string | null;
}

export interface AdminUserAccess {
  passes: AdminUserPass[];
  subscriptions: AdminUserSubscription[];
  grants: AdminUserGrant[];
  deck_expires_at: string | null;
}

export function useAdminUserAccess(userId: string | null) {
  return useQuery({
    queryKey: ['admin-user-access', userId],
    enabled: !!userId,
    queryFn: async (): Promise<AdminUserAccess> => {
      const { data, error } = await db.rpc('admin_get_user_access', { p_user_id: userId });
      if (error) throw new Error(error.message);
      return data as AdminUserAccess;
    },
  });
}

export interface GrantAccessInput {
  userId: string;
  accessTypes: GiftAccessType[];
  durationDays: number;
  extend: boolean;
  notify: { push: boolean; email: boolean; title: string; template: string };
}

export interface GrantAccessResult {
  ok: true;
  expires_at: string;
  message: string;
  push_sent: boolean;
  push_skipped: string | null;
  email_sent: boolean;
  email_skipped: string | null;
}

async function invokeAdminAccess<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-user-access', { body });
  if (error) {
    let code: string | null = null;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        code = ((await ctx.json()) as { error?: string }).error ?? null;
      } catch {
        /* corps illisible */
      }
    }
    throw new Error(code ?? error.message);
  }
  return data as T;
}

function invalidate(qc: ReturnType<typeof useQueryClient>, userId: string) {
  void qc.invalidateQueries({ queryKey: ['admin-users'] });
  void qc.invalidateQueries({ queryKey: ['admin-user-access', userId] });
}

export function useGrantAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: GrantAccessInput) =>
      invokeAdminAccess<GrantAccessResult>({
        action: 'grant',
        user_id: i.userId,
        access_types: i.accessTypes,
        duration_days: i.durationDays,
        extend: i.extend,
        notify: i.notify,
      }),
    onSettled: (_d, _e, i) => invalidate(qc, i.userId),
  });
}

export function useRevokeGift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { accessRightId: string; userId: string }) =>
      invokeAdminAccess<{ ok: true }>({ action: 'revoke', access_right_id: p.accessRightId }),
    onSettled: (_d, _e, p) => invalidate(qc, p.userId),
  });
}

/** Compte non confirmé : inscription jamais terminée (migration 0317). */
export interface PendingAccount {
  id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed: boolean;
  has_profile: boolean;
}

export const pendingAccountsKey = ['admin-pending-accounts'] as const;

export function usePendingAccounts() {
  return useQuery({
    queryKey: pendingAccountsKey,
    queryFn: async (): Promise<PendingAccount[]> => {
      const { data, error } = await db.rpc('admin_list_pending_accounts', { p_limit: 200 });
      if (error) throw new Error(error.message);
      return (data ?? []) as PendingAccount[];
    },
  });
}

/** Pourquoi le compte n'est pas confirmé (une ligne lisible). */
export function pendingAccountLabel(a: Pick<PendingAccount, 'email_confirmed' | 'has_profile'>) {
  if (!a.email_confirmed) return 'Email jamais vérifié';
  if (!a.has_profile) return 'Inscription non terminée';
  return 'Non confirmé';
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      invokeAdminAccess<{ ok: true }>({ action: 'delete', user_id: userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      void qc.invalidateQueries({ queryKey: pendingAccountsKey });
    },
  });
}

/** Message lisible pour un code d'erreur de l'edge fn. */
export function adminAccessErrorMessage(code: string): string {
  switch (code) {
    case 'forbidden':
      return 'Réservé aux administrateurs.';
    case 'user_not_found':
      return 'Ce compte n’existe plus.';
    case 'not_a_gift':
      return 'Seul un accès offert peut être retiré ici (les accès payés se gèrent dans Stripe).';
    case 'delete_not_allowed':
      return 'Seuls les comptes non confirmés, @jeraisonne.fr ou de test (hors admins et compte de revue) peuvent être supprimés.';
    case 'owns_content':
      return 'Ce compte a créé des SKILLCARDS, générateurs ou épreuves : supprime-les ou réattribue-les d’abord.';
    case 'invalid_input':
      return 'Vérifie la durée et le message.';
    default:
      return 'L’opération a échoué. Réessaie.';
  }
}
